import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ComponentType } from "react";
import { ArrowRight } from "lucide-react";

import { PageHero } from "@/components/site/Blocks";
import { ArticleTypeBadge } from "@/components/site/ArticleTypeBadge";
import { ShareLinks } from "@/components/site/ShareLinks";
import { ViDAOverviewGraphicEn } from "@/components/site/ViDAOverviewGraphicEn";
import { pageLinks } from "@/lib/locale-alternates";
import { breadcrumb, faqPage, graph, ORG_ID, url, webPage, WEBSITE_ID } from "@/lib/schema";
import { socialMeta, pageSeo } from "@/lib/seo";
import { founderRef } from "@/lib/person-schema";
import { formatEnDate, type EnBlogPost } from "@/lib/en-blog";
import { ArticleRichText } from "@/components/site/ArticleRichText";

export const Route = createFileRoute("/blog_/$slug")({
  loader: async ({ params }) => {
    const [{ getPublishedArticle }, { toEnBlogPost, enCategoryName }] = await Promise.all([
      import("@/lib/articles.functions"),
      import("@/lib/en-blog"),
    ]);
    const { article, others } = await getPublishedArticle({
      data: { locale: "en", slug: params.slug },
    });
    if (!article) throw notFound();
    return {
      post: toEnBlogPost(article),
      categorySlug: article.category ?? "",
      others: others.map(({ slug, title, category }) => ({
        slug,
        title,
        category: enCategoryName(category),
      })),
    };
  },

  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [{ title: "Article unavailable | OXORY" }, { name: "robots", content: "noindex" }],
      };
    }
    const path = `/blog/${params.slug}`;
    const metadata = pageSeo(path, { title: post.metaTitle, description: post.metaDescription });

    return {
      meta: [
        { title: metadata.title },
        { name: "description", content: metadata.description },
        { name: "keywords", content: post.keywords.join(", ") },
        ...socialMeta({
          title: metadata.title,
          description: metadata.description,
          path,
          type: "article",
          socialTitle: post.title,
          imageAlt: `OXORY blog article: ${post.title}`,
          articleId: post.articleIndex,
        }),
        { property: "article:published_time", content: post.published },
        { property: "article:modified_time", content: post.updated },
      ],
      links: pageLinks(path),
      scripts: [
        graph(
          { "@id": ORG_ID },
          { "@id": WEBSITE_ID },
          webPage({
            path,
            name: metadata.title,
            description: metadata.description,
            inLanguage: "en",
          }),
          breadcrumb(trailFor(post)),
          {
            "@type": "BlogPosting",
            "@id": `${url(path)}#article`,
            headline: post.title,
            description: metadata.description,
            inLanguage: "en",
            datePublished: post.published,
            dateModified: post.updated,
            author: founderRef("en"),
            publisher: { "@id": ORG_ID },
            keywords: post.keywords.join(", "),
            mainEntityOfPage: { "@id": `${url(path)}#webpage` },
            ...(post.articleIndex ? { identifier: post.articleIndex } : {}),
          },
          ...(import.meta.env.SSR ? [faqPage(post.faq)] : []),
        ),
      ],
    };
  },
  component: BlogArticle,
});

function trailFor(post: EnBlogPost) {
  return [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ];
}

/** Slug-specific inline graphics; other posts stay on the generic renderer. */
const EN_ARTICLE_GRAPHICS: Record<string, ComponentType> = {
  "vida-eu-vat-digital-age": ViDAOverviewGraphicEn,
};

function BlogArticle() {
  const { post, categorySlug, others } = Route.useLoaderData();
  const ArticleGraphic = EN_ARTICLE_GRAPHICS[post.slug];

  return (
    <>
      <PageHero eyebrow={post.category} title={post.title} intro={post.excerpt} trail={trailFor(post)} />

      <article className="section-shell py-16 md:py-20">
        <ArticleTypeBadge type="blog" locale="en" />
        <ShareLinks className="mt-4" path={`/blog/${post.slug}`} title={post.title} locale="en" />
        <p className="mt-3 text-sm text-muted-foreground">
          <time dateTime={post.published}>{formatEnDate(post.published)}</time>
          {post.articleIndex && (
            <>
              <span aria-hidden="true"> · </span>
              {post.articleIndex}
            </>
          )}
          <span aria-hidden="true"> · </span>
          {post.readingMinutes} min read
          <span aria-hidden="true"> · </span>
          Updated: <time dateTime={post.updated}>{formatEnDate(post.updated)}</time>
          {categorySlug && (
            <>
              <span aria-hidden="true"> · </span>
              Category:{" "}
              <Link
                to="/blog/category/$slug"
                params={{ slug: categorySlug }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {post.category}
              </Link>
            </>
          )}
        </p>

        <div className="mt-10 max-w-3xl space-y-12">
          {ArticleGraphic && <ArticleGraphic />}

          {post.sections.map((section) => (
            <section key={section.heading}>
              {section.heading && <h2 className="text-2xl font-bold md:text-3xl">{section.heading}</h2>}
              {section.paragraphs.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 40)}
                  className="mt-4 text-lg leading-relaxed text-muted-foreground"
                >
                  <ArticleRichText text={paragraph} />
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-5 space-y-3">
                  {section.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 40)} className="flex gap-3 text-muted-foreground">
                      <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                      <span><ArticleRichText text={bullet} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {post.faq.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold md:text-3xl">Frequently asked questions</h2>
              <dl className="mt-6 space-y-6">
                {post.faq.map(([question, answer]) => (
                  <div key={question}>
                    <dt className="font-semibold">{question}</dt>
                    <dd className="mt-2 text-muted-foreground">{answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>

        <div className="mt-14 rounded-xl border border-border bg-muted/40 p-8">
          <h2 className="text-2xl font-bold">Next step</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Let's work out how this applies to your own SAP landscape and delivery plan.
          </p>
          <Link
            to={post.cta.to}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground"
          >
            {post.cta.label}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>

        {others.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold">More articles</h2>
            <ul className="mt-6 grid gap-6 md:grid-cols-3">
              {others.map((other) => (
                <li key={other.slug} className="rounded-xl border border-border bg-card p-5">
                  <p className="eyebrow text-primary">— {other.category}</p>
                  <h3 className="mt-2 text-lg font-bold leading-snug">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: other.slug }}
                      className="underline-offset-4 hover:underline"
                    >
                      {other.title}
                    </Link>
                  </h3>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}
